import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { entitlementsApi } from '@/api/entitlements';
import { LogoMainHorizontal } from '@/components/brand';
import { ArrowRight, Spinner } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { ENTITLEMENTS_QUERY_KEY, useEntitlements } from '@/hooks/use-entitlements';
import { useWelcomeScreen } from '@/hooks/use-welcome-screen';
import { getTrialRemainingDays } from '@/lib/entitlements';
import { capture } from '@/lib/posthog';
import { BillingPage, DashboardPage, ReportPage } from '@/pages/constants';
import { createPageUrl } from '@/utils';

const getFirstName = (fullName?: string | null) => {
    const firstMeaningfulName = fullName
        ?.split(' ')
        .find((part) => !['dr.', 'dra.', 'dr', 'dra'].includes(part.toLowerCase()));

    return firstMeaningfulName || 'Marina';
};

const getPlanMessage = (entitlements: ReturnType<typeof useEntitlements>['entitlements']) => {
    if (!entitlements) {
        return 'Sua conta já está pronta para explorar a plataforma.';
    }

    if (entitlements.plan === 'TRIAL' && entitlements.status === 'trial') {
        const remainingDays = getTrialRemainingDays(entitlements.period_end);

        return (
            <>
                Sua conta está em período de teste
                {remainingDays !== null ? (
                    <>
                        {' '}
                        por{' '}
                        <strong className="font-semibold text-foreground">
                            {remainingDays} dias
                        </strong>
                    </>
                ) : null}
                . Acesso liberado aos recursos disponíveis no teste.
            </>
        );
    }

    if (entitlements.plan === 'TRIAL' && entitlements.status === 'trial_not_started') {
        return 'Você ainda não iniciou o teste grátis. Para acessar a plataforma, escolha começar o teste ou conclua uma assinatura.';
    }

    if (entitlements.plan !== 'TRIAL' && entitlements.active) {
        return (
            <>
                Seu plano{' '}
                <strong className="font-semibold text-foreground">{entitlements.name}</strong> está
                ativo. Acesso liberado aos recursos disponíveis na sua assinatura.
            </>
        );
    }

    return 'Sua conta já está pronta para explorar a plataforma.';
};

export default function WelcomePage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const { entitlements } = useEntitlements();
    const { completeWelcome, hasCompletedWelcome, isCompletingWelcome } = useWelcomeScreen();
    const [isFinishing, setIsFinishing] = useState(false);
    const [isStartingTrial, setIsStartingTrial] = useState(false);

    const firstName = useMemo(() => getFirstName(user?.full_name), [user?.full_name]);
    const planMessage = getPlanMessage(entitlements);

    useEffect(() => {
        if (hasCompletedWelcome && !isFinishing) {
            navigate(createPageUrl(DashboardPage), { replace: true });
        }
    }, [hasCompletedWelcome, isFinishing, navigate]);

    const completeAndNavigate = async (destination: string) => {
        try {
            setIsFinishing(true);
            await completeWelcome();
            navigate(destination, { replace: true });
        } catch (error) {
            setIsFinishing(false);
            console.error('Failed to complete welcome screen', error);
            toast.error('Não foi possível continuar agora. Tente novamente.');
        }
    };

    const startTrialAndNavigate = async () => {
        try {
            // The click, not the outcome — the API owns `trial_started`.
            capture('welcome_trial_cta_clicked', {});
            setIsStartingTrial(true);
            await entitlementsApi.optInTrial();
            await queryClient.invalidateQueries({
                queryKey: ENTITLEMENTS_QUERY_KEY,
            });
            setIsFinishing(true);
            await completeWelcome();
            navigate(createPageUrl(DashboardPage), { replace: true });
        } catch (error) {
            setIsStartingTrial(false);
            setIsFinishing(false);
            console.error('Failed to start trial', error);
            toast.error('Não foi possível iniciar o teste grátis agora.');
        }
    };

    const requiresTrialDecision = entitlements?.status === 'trial_not_started';

    return (
        <main className="relative flex min-h-screen overflow-hidden bg-background px-6 py-16 text-foreground sm:px-8 lg:px-10">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                <div className="absolute -left-56 -top-64 size-[44rem] rounded-full bg-brand-subtle/70 blur-3xl" />
                <div className="absolute -bottom-56 -right-48 size-[40rem] rounded-full bg-brand-subtle/70 blur-3xl" />
                <div className="absolute right-[18%] top-[30%] size-[30rem] rounded-full bg-brand-subtle/50 blur-3xl" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-white/80" />
            </div>

            <section className="relative z-10 mx-auto flex w-full max-w-xl items-center">
                <div className="flex w-full flex-col gap-6 duration-500 animate-in fade-in-0 slide-in-from-bottom-3">
                    <LogoMainHorizontal />

                    <div className="space-y-4">
                        <h1 className="text-balance text-[2rem] font-semibold leading-tight tracking-[-0.025em] text-foreground sm:text-[2.35rem]">
                            Boas-vindas ao LaudoPsi, <span className="text-brand">{firstName}</span>
                            .
                        </h1>
                        <p className="text-pretty text-base leading-8 text-muted-foreground">
                            O LaudoPsi foi criado para ajudar psicólogos a construir documentos com
                            mais organização, agilidade e segurança.
                        </p>
                        <p className="text-pretty text-base leading-8 text-muted-foreground">
                            {planMessage}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 pt-1">
                        {requiresTrialDecision ? (
                            <>
                                <Button
                                    size="lg"
                                    onClick={startTrialAndNavigate}
                                    disabled={isCompletingWelcome || isStartingTrial}
                                >
                                    {isStartingTrial ? (
                                        <Spinner className="size-4 animate-spin" />
                                    ) : null}
                                    Começar teste grátis
                                    {!isStartingTrial ? <ArrowRight className="size-4" /> : null}
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="lg"
                                    onClick={() => navigate(createPageUrl(BillingPage))}
                                    disabled={isCompletingWelcome || isStartingTrial}
                                >
                                    Ver planos
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    size="lg"
                                    onClick={() =>
                                        completeAndNavigate(createPageUrl(DashboardPage))
                                    }
                                    disabled={isCompletingWelcome}
                                >
                                    {isCompletingWelcome
                                        ? 'Preparando...'
                                        : 'Explorar a plataforma'}
                                    {!isCompletingWelcome ? (
                                        <ArrowRight className="size-4" />
                                    ) : null}
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="lg"
                                    onClick={() => completeAndNavigate(createPageUrl(ReportPage))}
                                    disabled={isCompletingWelcome}
                                >
                                    Criar meu primeiro laudo
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}
